import React, { useEffect, useId } from 'react';
import { X } from 'lucide-react';

// Small shared pieces of the app's look (the class names they use are in index.css).

export const Spinner: React.FC<{ className?: string }> = ({ className = 'w-5 h-5' }) => (
  <span
    className={`inline-block shrink-0 animate-spin rounded-full border-2 border-current border-r-transparent text-blue-600 dark:text-blue-400 ${className}`}
    aria-hidden="true"
  />
);

// A whole area that is still loading: a spinner with a line of text, announced to screen readers.
export const LoadingState: React.FC<{ label?: string; className?: string }> = ({ label = 'Loading...', className = 'py-16' }) => (
  <div role="status" className={`flex flex-col items-center justify-center gap-3 text-sm font-medium text-gray-500 dark:text-gray-400 ${className}`}>
    <Spinner className="w-7 h-7" />
    <span>{label}</span>
  </div>
);

// Nothing to show yet: an icon, a title, a line of help and optionally what to do next.
export const EmptyState: React.FC<{
  icon?: React.ReactNode;
  title: React.ReactNode;
  children?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
}> = ({ icon, title, children, action, className = '' }) => (
  <div className={`flex flex-col items-center rounded-xl border border-dashed border-gray-300 dark:border-gray-700 px-6 py-10 sm:py-14 text-center ${className}`}>
    {icon && (
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400 [&_svg]:w-6 [&_svg]:h-6">
        {icon}
      </div>
    )}
    <p className="text-base font-semibold text-gray-900 dark:text-white">{title}</p>
    {children && <div className="mt-1 max-w-md text-sm text-gray-500 dark:text-gray-400">{children}</div>}
    {action && <div className="mt-5">{action}</div>}
  </div>
);

// A page's title row: the title and a line under it on the left, the page's main actions on the right.
export const PageHeader: React.FC<{
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  actions?: React.ReactNode;
  children?: React.ReactNode;
  as?: 'h1' | 'h2';
}> = ({ title, subtitle, actions, children, as: Heading = 'h2' }) => (
  <div className="page-header">
    <div className="min-w-0">
      <Heading className="page-title">{title}</Heading>
      {subtitle && <p className="page-subtitle">{subtitle}</p>}
      {children}
    </div>
    {actions && <div className="flex flex-wrap items-center gap-2 shrink-0">{actions}</div>}
  </div>
);

// A centred dialog over a dimmed page. Escape and the close button call onClose; a click on the backdrop doesn't
// (forms in dialogs would lose their input to a stray click).
export const Modal: React.FC<{
  title: React.ReactNode;
  description?: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'md' | 'lg';
  zIndex?: string;
}> = ({ title, description, onClose, children, footer, size = 'md', zIndex = 'z-50' }) => {
  const titleId = useId();
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  return (
    <div className={`modal-backdrop ${zIndex}`}>
      <div role="dialog" aria-modal="true" aria-labelledby={titleId} className={`modal-panel ${size === 'lg' ? 'max-w-2xl' : 'max-w-md'}`}>
        <div className="mb-5 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h3 id={titleId} className="modal-title">{title}</h3>
            {description && <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">{description}</div>}
          </div>
          <button type="button" onClick={onClose} className="btn btn-ghost btn-icon -mr-2 -mt-1" aria-label="Close dialog">
            <X />
          </button>
        </div>
        {children}
        {footer && <div className="modal-actions">{footer}</div>}
      </div>
    </div>
  );
};
